import { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { qtyDisplay } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Loader2, Check, Clock, Package, Truck, MapPin, Stethoscope, CreditCard, PawPrint, Pill } from "lucide-react";

const STATUS_STEPS = [
  { key: "pending_payment", label: "Payment", icon: CreditCard },
  { key: "pending_vet_review", label: "Quality Check", icon: Clock },
  { key: "cosigned", label: "Approved", icon: Stethoscope },
  { key: "sent_to_fulfillment", label: "Fulfillment", icon: Package },
  { key: "shipped", label: "Shipped", icon: Truck },
  { key: "delivered", label: "Delivered", icon: MapPin },
];

function getStepIndex(status) {
  const map = {
    pending_payment: 0, paid: 0,
    pending_vet_review: 1, under_vet_review: 1,
    vet_approved: 2, cosigned: 2, vet_modification_requested: 1,
    sent_to_fulfillment: 3, being_prepared: 3, processing: 3,
    shipped: 4, delivered: 5, completed: 5,
    vet_rejected: -1, rejected: -1, cancelled: -1, refunded: -1,
  };
  return map[status] ?? 0;
}

export default function OrderDetail() {
  const { orderId } = useParams();
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!authLoading && (!user || user === false)) navigate("/login");
  }, [authLoading, user, navigate]);

  useEffect(() => {
    if (user && user !== false && orderId) fetchOrder();
  }, [user, orderId]);

  const fetchOrder = async () => {
    try {
      const { data } = await api.get(`/orders/${orderId}`);
      setOrder(data);
    } catch {
      navigate("/dashboard");
    } finally {
      setLoading(false);
    }
  };

  if (authLoading || loading) {
    return <div className="min-h-[60vh] flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-paw-primary" /></div>;
  }

  if (!order) return null;

  const currentStep = getStepIndex(order.status);
  const isRejected = order.status === "rejected";

  return (
    <div data-testid="order-detail-page" className="py-12 md:py-20">
      <div className="max-w-3xl mx-auto px-6">
        <Link to="/dashboard" className="inline-flex items-center text-sm text-paw-text-secondary hover:text-paw-primary font-manrope mb-6 transition-colors" data-testid="back-to-dashboard">
          <ArrowLeft className="w-4 h-4 mr-1" /> Back to Dashboard
        </Link>

        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="font-outfit text-2xl font-bold text-paw-text">{order.order_number}</h1>
            <p className="font-manrope text-sm text-paw-text-muted">
              Placed on {new Date(order.created_at).toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric", year: "numeric" })}
            </p>
          </div>
        </div>

        {/* Status Timeline */}
        {!isRejected && (
          <div className="bg-white rounded-2xl border border-paw-border p-6 md:p-8 mb-6" data-testid="order-status-timeline">
            <h2 className="font-outfit font-semibold text-base text-paw-text mb-6">Order Status</h2>
            <div className="flex items-center justify-between relative">
              <div className="absolute top-5 left-8 right-8 h-0.5 bg-paw-border" />
              <div className="absolute top-5 left-8 h-0.5 bg-paw-success transition-all duration-500" style={{ width: `${Math.max(0, (currentStep / (STATUS_STEPS.length - 1)) * 100 - 5)}%` }} />
              {STATUS_STEPS.map((s, i) => (
                <div key={s.key} className="flex flex-col items-center relative z-10">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center transition-all duration-300 ${i < currentStep ? "bg-paw-success text-white" : i === currentStep ? "bg-paw-primary text-white ring-4 ring-paw-primary/20" : "bg-paw-surface-alt text-paw-text-muted"}`}>
                    {i < currentStep ? <Check className="w-4 h-4" /> : <s.icon className="w-4 h-4" />}
                  </div>
                  <span className={`font-manrope text-xs mt-2 whitespace-nowrap ${i <= currentStep ? "text-paw-text font-medium" : "text-paw-text-muted"}`}>{s.label}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {isRejected && (
          <div className="bg-paw-error/5 border border-paw-error/20 rounded-2xl p-6 mb-6" data-testid="order-rejected-notice">
            <h2 className="font-outfit font-semibold text-paw-error mb-2">Prescription Rejected</h2>
            <p className="font-manrope text-sm text-paw-text-secondary">Your prescription has been reviewed and could not be approved. Our team will reach out with next steps.</p>
          </div>
        )}

        {/* Order Details */}
        <div className="space-y-4">
          <div className="bg-white rounded-2xl border border-paw-border p-6">
            <h3 className="font-outfit font-semibold text-sm text-paw-text-muted uppercase tracking-wider mb-4 flex items-center gap-2">
              <PawPrint className="w-4 h-4" /> Pet Information
            </h3>
            <div className="grid grid-cols-2 gap-4 font-manrope text-sm">
              <div><span className="text-paw-text-muted">Name:</span> <span className="text-paw-text font-medium ml-1">{order.pet?.name}</span></div>
              <div><span className="text-paw-text-muted">Species:</span> <span className="text-paw-text font-medium ml-1 capitalize">{order.pet?.species}</span></div>
              {order.pet?.breed && <div><span className="text-paw-text-muted">Breed:</span> <span className="text-paw-text font-medium ml-1">{order.pet?.breed}</span></div>}
              {order.pet?.weight > 0 && <div><span className="text-paw-text-muted">Weight:</span> <span className="text-paw-text font-medium ml-1">{order.pet?.weight} {order.pet?.weight_unit}</span></div>}
            </div>
          </div>

          <div className="bg-white rounded-2xl border border-paw-border p-6">
            <h3 className="font-outfit font-semibold text-sm text-paw-text-muted uppercase tracking-wider mb-4 flex items-center gap-2">
              <Pill className="w-4 h-4" /> Medication
            </h3>
            <div className="grid grid-cols-2 gap-4 font-manrope text-sm">
              <div><span className="text-paw-text-muted">Name:</span> <span className="text-paw-text font-medium ml-1">{order.medication?.name}</span></div>
              <div><span className="text-paw-text-muted">Dosage:</span> <span className="text-paw-text font-medium ml-1">{order.medication?.dosage}</span></div>
              <div><span className="text-paw-text-muted">Quantity:</span> <span className="text-paw-text font-medium ml-1">{qtyDisplay(order.medication)}</span></div>
            </div>
          </div>

          <div className="bg-white rounded-2xl border border-paw-border p-6">
            <h3 className="font-outfit font-semibold text-sm text-paw-text-muted uppercase tracking-wider mb-4 flex items-center gap-2">
              <MapPin className="w-4 h-4" /> Shipping Address
            </h3>
            <p className="font-manrope text-sm text-paw-text">{order.shipping_address?.street}</p>
            <p className="font-manrope text-sm text-paw-text-secondary">
              {order.shipping_address?.city}, {order.shipping_address?.state_province} {order.shipping_address?.postal_code}
            </p>
            <p className="font-manrope text-sm text-paw-text-secondary">{order.shipping_address?.country === "CA" ? "Canada" : "United States"}</p>
          </div>

          {order.tracking?.tracking_number && (
            <div className="bg-paw-primary/5 border border-paw-primary/20 rounded-2xl p-6">
              <h3 className="font-outfit font-semibold text-sm text-paw-primary uppercase tracking-wider mb-3 flex items-center gap-2">
                <Truck className="w-4 h-4" /> Tracking Information
              </h3>
              <p className="font-manrope text-sm text-paw-text">Carrier: <span className="font-medium">{order.tracking.carrier}</span></p>
              <p className="font-manrope text-sm text-paw-text">Tracking #: <span className="font-medium">{order.tracking.tracking_number}</span></p>
            </div>
          )}

          <div className="bg-white rounded-2xl border border-paw-border p-6">
            <h3 className="font-outfit font-semibold text-sm text-paw-text-muted uppercase tracking-wider mb-4 flex items-center gap-2">
              <CreditCard className="w-4 h-4" /> Payment
            </h3>
            <div className="flex justify-between items-center">
              <span className="font-manrope text-sm text-paw-text-secondary">Service & Processing Fee</span>
              <span className="font-outfit font-bold text-lg text-paw-text">${order.payment?.amount} {order.payment?.currency?.toUpperCase()}</span>
            </div>
            <p className="font-manrope text-xs text-paw-text-muted mt-1">
              Status: <span className="capitalize font-medium">{order.payment?.status}</span>
            </p>
            {(order.payment?.status === "completed" || order.payment?.status === "refunded") && (
              <a
                href={`${process.env.REACT_APP_BACKEND_URL}/api/orders/${order.order_id}/invoice`}
                target="_blank" rel="noreferrer"
                className="mt-4 w-full inline-flex items-center justify-center gap-2 bg-paw-primary hover:bg-paw-primary-hover text-white rounded-xl h-11 font-manrope font-semibold text-sm transition-colors"
                data-testid="view-invoice-btn"
              >
                View / Download Invoice
              </a>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
